// @ts-strict-ignore
import React, { useContext, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';
import {
  getMultiSelectProps,
  getOperatorOptions,
  getSelectedOperatorOption,
  hasAttributeOptions,
} from './helpers';
import { Tooltip } from '@usealloy/component-library';
import { updateThreshold } from 'flow/store/redux/thunks/thresholdUpdates';
import type { DropdownOption } from 'flow/editor/components/NodeEditorDrawer/types';
import { ThresholdContext } from './context';
import { StyledCodeStyleText } from 'flow/editor/styles';
import RuleMultiSelect from '../RuleTemplate/RuleMultiSelect';
import { RuleBuilderContext } from '../../context';
import { StyledEditableControlContainer } from '../styles';
import { selectRuleBuilderActiveSubEditor } from 'flow/store/redux/selectors';
import { getIsPreview } from '../../helpers';
import { UserContext } from '~/dashboard/context';
import { createWorkflowHistoryThunk } from 'flow/store/redux/thunks';
import { isPreviewAction } from 'flow/service/actions';
import { Operation } from 'flow/service/changelog/types';
import { createUiEffectContext } from 'flow/service/history';
import { changeDescription } from 'flow/store/redux/types';
import type { WorkflowStateChange } from 'flow/store/redux/types';

export interface SelectedOperator {
  value: string;
  meta?: {
    RHS: string | number | null;
  };
}

interface OperatorWrapperProps {
  operatorIsNullOrNotNull: boolean;
}

const OperatorWrapper = ({ operatorIsNullOrNotNull }: OperatorWrapperProps) => {
  const dispatch = useAppDispatch();
  const activeSubEditor = useAppSelector(selectRuleBuilderActiveSubEditor);
  const { featureFlags } = useContext(UserContext);
  const { editMode, sourceAction } = useContext(RuleBuilderContext);
  const { isDynamic, lhsDataType, populatedThreshold } = useContext(ThresholdContext);
  const { left, publicId } = populatedThreshold;

  const selectedOperator = operatorIsNullOrNotNull
    ? { value: populatedThreshold.operator, meta: { RHS: populatedThreshold.right.attributeValue } }
    : { value: populatedThreshold.operator };

  const leftHasAttributeOptions = hasAttributeOptions(left.attribute);

  const operatorOptions = getOperatorOptions({
    actionType: left.action.type,
    isDynamic,
    leftHasAttributeOptions,
    lhsDataType,
    supportsBestEffortParsing: featureFlags.allowBestEffortRuleEvaluations,
  });

  const selectedOperatorOption = getSelectedOperatorOption(
    selectedOperator,
    operatorOptions,
  );

  const handleDispatch = (stateChange: WorkflowStateChange) => {
    dispatch(createWorkflowHistoryThunk(
      stateChange,
      Boolean(featureFlags?.workflowEditorUndoRedo) && !isPreviewAction(sourceAction),
      {
        operation: Operation.Modify,
        uiEffectContext: createUiEffectContext(changeDescription.threshold, {
          publicId,
          actionId: populatedThreshold.actionId,
        }),
      },
    ));
  };

  const handleOperatorChange = (option: DropdownOption) => {
    const { value: newOperator, meta } = option;

    handleDispatch(() => {
      dispatch(updateThreshold.operator(publicId, newOperator, meta?.RHS));
    });
  };

  const multiSelectProps = getMultiSelectProps({
    options: {
      isPreview: getIsPreview(editMode, populatedThreshold, activeSubEditor.thresholdId),
    },
  });

  const tooltipContent = useMemo(() => {
    if (operatorIsNullOrNotNull) {
      return (
        <StyledCodeStyleText>
          This refers to empty string, null, or undefined.
        </StyledCodeStyleText>
      );
    }
    return null;
  }, [operatorIsNullOrNotNull]);

  return (
    <Tooltip
      align="start"
      content={tooltipContent}
      side="top"
      variant="secondary"
      hide={!tooltipContent}
    >
      <StyledEditableControlContainer>
        <RuleMultiSelect
          {...multiSelectProps}
          options={operatorOptions}
          onChange={handleOperatorChange}
          selectedOptions={selectedOperatorOption}
          isMulti={false}
          isNaked
          openMenuOnFocus
          placeholder="..."
        />
      </StyledEditableControlContainer>
    </Tooltip>
  );
};

export default OperatorWrapper;
